import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n/config';
import { personalInfo } from '../../data/portfolio';
import { useThemeMode } from '../../context/ThemeContext';

const SidebarWrapper = styled.aside`
  position: sticky;
  top: 0;
  height: 100vh;
  width: 300px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 80px 32px 48px;

  @media (max-width: 1080px) {
    display: none;
  }
`;

const TopSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 18px;
`;

const ProfilePhoto = styled.img`
  width: 68px;
  height: 68px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid rgba(125, 211, 252, 0.3);
`;

const NameBlock = styled.div``;

const Name = styled.h1`
  font-size: 24px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text};
  margin: 0 0 4px 0;
  line-height: 1.2;
`;

const RoleWrapper = styled.div`
  height: 20px;
  overflow: hidden;
`;

const RoleText = styled(motion.span)`
  display: block;
  font-size: 13px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.accent};
`;

const Bio = styled.p`
  font-size: 13px;
  color: var(--clr-text-muted);
  line-height: 1.6;
  margin: 0;
`;

const Nav = styled.nav`
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin-top: 8px;
`;

const NavItem = styled.a<{ $active: boolean }>`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 7px 0;
  text-decoration: none;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover span:last-child {
    color: ${({ theme }) => theme.colors.text};
  }

  &:hover span:first-child {
    width: 24px;
    background: var(--clr-accent-mid);
  }
`;

const NavLine = styled.span<{ $active: boolean }>`
  width: ${({ $active }) => ($active ? '28px' : '10px')};
  height: 2px;
  border-radius: 2px;
  background: ${({ $active }) =>
    $active ? '#7dd3fc' : 'var(--clr-text-hint)'};
  transition: all 0.3s ease;
  flex-shrink: 0;
`;

const NavLabel = styled.span<{ $active: boolean }>`
  font-size: 11px;
  font-weight: ${({ $active }) => ($active ? '700' : '400')};
  color: ${({ $active, theme }) => ($active ? theme.colors.text : 'var(--clr-text-faint)')};
  letter-spacing: 1.4px;
  text-transform: uppercase;
  transition: color 0.2s ease;
`;

const BottomSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const Socials = styled.div`
  display: flex;
  gap: 18px;
  align-items: center;
`;

const LangSwitcher = styled.div`
  display: flex;
  gap: 4px;
  align-items: center;
`;

const LangBtn = styled.button<{ $active: boolean }>`
  background: none;
  border: 1px solid ${({ $active }) => ($active ? 'var(--clr-accent-border-h)' : 'var(--clr-text-hint)')};
  color: ${({ $active, theme }) => ($active ? theme.colors.accent : 'var(--clr-text-faint)')};
  border-radius: 4px;
  padding: 3px 8px;
  font-size: 11px;
  font-weight: ${({ $active }) => ($active ? '700' : '400')};
  letter-spacing: 0.5px;
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: inherit;

  &:hover {
    color: ${({ theme }) => theme.colors.text};
    border-color: var(--clr-accent-border-h);
  }
`;

const ThemeToggleBtn = styled.button`
  background: none;
  border: 1px solid var(--clr-text-hint);
  color: var(--clr-text-faint);
  border-radius: 4px;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  padding: 0;
  flex-shrink: 0;

  &:hover {
    color: ${({ theme }) => theme.colors.accent};
    border-color: var(--clr-accent-border-h);
  }
`;

const SocialLink = styled.a`
  color: var(--clr-text-faint);
  transition: color 0.2s ease;
  display: flex;
  align-items: center;
  line-height: 1;

  &:hover {
    color: ${({ theme }) => theme.colors.accent};
  }
`;

const navIds = ['about', 'experience', 'education', 'achievements', 'contact'] as const;

const Sidebar: React.FC = () => {
  const { t } = useTranslation();
  const { mode, toggleTheme } = useThemeMode();
  const [activeSection, setActiveSection] = useState('about');
  const [roleIndex, setRoleIndex] = useState(0);
  const [lang, setLang] = useState(i18n.language);

  const switchLang = (l: string) => {
    i18n.changeLanguage(l);
    localStorage.setItem('portfolio-lang', l);
    setLang(l);
  };

  const roles: string[] = t('hero.roles', { returnObjects: true }) as string[];
  const navItems = navIds.map((id) => ({ id, label: t(`nav.${id}`) }));
  const roleCount = roles.length;

  useEffect(() => {
    const interval = setInterval(() => {
      setRoleIndex((prev) => (prev + 1) % roleCount);
    }, 3000);
    return () => clearInterval(interval);
  }, [roleCount]);

  useEffect(() => {
    const sectionVisibility: Record<string, number> = {};
    const observers: IntersectionObserver[] = [];

    navIds.forEach((id) => {
      const el = document.getElementById(id);
      if (!el) return;
      const observer = new IntersectionObserver(
        ([entry]) => {
          sectionVisibility[id] = entry.intersectionRatio;
          const best = Object.entries(sectionVisibility).sort(
            (a, b) => b[1] - a[1]
          )[0];
          if (best && best[1] > 0) setActiveSection(best[0]);
        },
        { threshold: [0, 0.1, 0.25, 0.5] }
      );
      observer.observe(el);
      observers.push(observer);
    });

    return () => observers.forEach((o) => o.disconnect());
  }, []);

  const scrollTo = (e: React.MouseEvent, id: string) => {
    e.preventDefault();
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <SidebarWrapper>
      <TopSection>
        <ProfilePhoto src={personalInfo.profileImage} alt={personalInfo.name} />
        <NameBlock>
          <Name>{personalInfo.name}</Name>
          <RoleWrapper>
            <AnimatePresence mode="wait">
              <RoleText
                key={roleIndex}
                initial={{ y: 14, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ y: -14, opacity: 0 }}
                transition={{ duration: 0.3 }}
              >
                {roles[roleIndex]}
              </RoleText>
            </AnimatePresence>
          </RoleWrapper>
        </NameBlock>
        <Bio>{t('hero.tagline')}</Bio>
        <Nav>
          {navItems.map(({ id, label }) => (
            <NavItem
              key={id}
              href={`#${id}`}
              $active={activeSection === id}
              onClick={(e) => scrollTo(e, id)}
            >
              <NavLine $active={activeSection === id} />
              <NavLabel $active={activeSection === id}>{label}</NavLabel>
            </NavItem>
          ))}
        </Nav>
      </TopSection>

      <BottomSection>
      <LangSwitcher>
        <ThemeToggleBtn onClick={toggleTheme} aria-label={mode === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'} title={mode === 'dark' ? 'Light mode' : 'Dark mode'}>
          {mode === 'dark' ? (
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="5"/>
              <line x1="12" y1="1" x2="12" y2="3"/>
              <line x1="12" y1="21" x2="12" y2="23"/>
              <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/>
              <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/>
              <line x1="1" y1="12" x2="3" y2="12"/>
              <line x1="21" y1="12" x2="23" y2="12"/>
              <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/>
              <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>
            </svg>
          ) : (
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
            </svg>
          )}
        </ThemeToggleBtn>
        <LangBtn $active={lang === 'en'} onClick={() => switchLang('en')}>EN</LangBtn>
        <LangBtn $active={lang === 'fr'} onClick={() => switchLang('fr')}>FR</LangBtn>
      </LangSwitcher>
      <Socials>
        {personalInfo.contact.github && (
          <SocialLink
            href={personalInfo.contact.github}
            target="_blank"
            rel="noopener noreferrer"
            title="GitHub"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z" />
            </svg>
          </SocialLink>
        )}
        {personalInfo.contact.linkedin && (
          <SocialLink
            href={personalInfo.contact.linkedin}
            target="_blank"
            rel="noopener noreferrer"
            title="LinkedIn"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
            </svg>
          </SocialLink>
        )}
        {personalInfo.contact.gitlab && (
          <SocialLink
            href={personalInfo.contact.gitlab}
            target="_blank"
            rel="noopener noreferrer"
            title="GitLab"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M22.65 14.39L12 22.13 1.35 14.39a.84.84 0 0 1-.3-.94l1.22-3.78 2.44-7.51A.42.42 0 0 1 4.82 2a.43.43 0 0 1 .58 0 .42.42 0 0 1 .11.18l2.44 7.49h8.1l2.44-7.51A.42.42 0 0 1 18.6 2a.43.43 0 0 1 .58 0 .42.42 0 0 1 .11.18l2.44 7.51L23 13.45a.84.84 0 0 1-.35.94z" />
            </svg>
          </SocialLink>
        )}
        {personalInfo.contact.email && (
          <SocialLink
            href={`mailto:${personalInfo.contact.email}`}
            title="Email"
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <rect x="2" y="4" width="20" height="16" rx="2" />
              <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
            </svg>
          </SocialLink>
        )}
      </Socials>
      </BottomSection>
    </SidebarWrapper>
  );
};

export default Sidebar;
